import { useState } from "react";
import { format } from "date-fns";
import {
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import MDBox from "components/MDBox";
import MDAvatar from "components/MDAvatar";
import MDDialog from "components/MDDialog";
import { LojaDataGrid } from "layouts/pages/loja/common";
import { cnpjApplyMask, cpfApplyMask, phoneApplyMask } from "helpers/utils";
import { IListOrganizador } from "./interfaces";
import { ORGANIZADORES_ACCENT } from "../organizadores.theme";

const ROW_HEIGHT = 68;

interface Props {
  organizadores: IListOrganizador[];
  onEdit: (organizador: IListOrganizador) => void;
  onDelete: (id: number) => void;
}

export default function TableOrganizador({
  organizadores,
  onEdit,
  onDelete,
}: Props): JSX.Element {
  const [deleteTarget, setDeleteTarget] = useState<IListOrganizador | null>(null);

  const handleDeleteConfirm = async (confirm: boolean) => {
    if (confirm && deleteTarget) {
      await onDelete(deleteTarget.id);
    }
    setDeleteTarget(null);
  };

  const columns: GridColDef<IListOrganizador>[] = [
    {
      field: "avatar",
      headerName: "",
      width: 72,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <MDBox display="flex" alignItems="center" justifyContent="center" height="100%" width="100%">
          <MDAvatar
            src={params.row.user?.avatar}
            alt={params.row.nomeFantasia}
            size="md"
            bgColor="secondary"
          />
        </MDBox>
      ),
    },
    {
      field: "nomeFantasia",
      headerName: "Nome fantasia",
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={700} noWrap title={params.row.nomeFantasia}>
          {params.row.nomeFantasia}
        </Typography>
      ),
    },
    {
      field: "razaoSocial",
      headerName: "Razão social",
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <Typography variant="body2" noWrap title={params.row.razaoSocial}>
          {params.row.razaoSocial}
        </Typography>
      ),
    },
    {
      field: "email",
      headerName: "E-mail",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Typography variant="body2" noWrap title={params.row.email}>
          {params.row.email}
        </Typography>
      ),
    },
    {
      field: "documento",
      headerName: "CPF/CNPJ",
      width: 150,
      valueGetter: (params) => params.row.cnpj || params.row.cpf,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
          {params.row.cnpj
            ? cnpjApplyMask(params.row.cnpj)
            : cpfApplyMask(params.row.cpf)}
        </Typography>
      ),
    },
    {
      field: "celPhone1",
      headerName: "Telefone",
      width: 130,
      renderCell: (params) => phoneApplyMask(params.row.celPhone1),
    },
    {
      field: "cidade",
      headerName: "Cidade",
      width: 120,
      valueGetter: (params) => params.row.enderecos?.[0]?.cidade ?? "—",
      renderCell: (params) => {
        const end = params.row.enderecos?.[0];
        return end ? `${end.cidade}/${end.uf.toUpperCase()}` : "—";
      },
    },
    {
      field: "eventos",
      headerName: "Eventos",
      width: 90,
      align: "center",
      headerAlign: "center",
      valueGetter: (params) => params.row._count?.eventos ?? 0,
    },
    {
      field: "createAt",
      headerName: "Cadastro",
      width: 110,
      renderCell: (params) => format(new Date(params.row.createAt), "dd/MM/yyyy"),
    },
    {
      field: "actions",
      headerName: "",
      width: 100,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title="Editar organizador">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(params.row);
              }}
            >
              <EditOutlinedIcon fontSize="small" sx={{ color: ORGANIZADORES_ACCENT }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir organizador">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteTarget(params.row);
              }}
            >
              <DeleteOutlineIcon fontSize="small" color="error" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <MDBox>
      <LojaDataGrid
        rows={organizadores}
        columns={columns}
        rowHeight={ROW_HEIGHT}
        onRowClick={(params) => onEdit(params.row)}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        localeText={{ noRowsLabel: "Nenhum organizador encontrado" }}
        sx={{
          border: "1px solid",
          borderColor: "grey.200",
          borderRadius: 2,
          "& .MuiDataGrid-row": { cursor: "pointer" },
          "& .MuiDataGrid-row:hover": {
            bgcolor: "rgba(249, 96, 48, 0.04)",
          },
          "& .MuiDataGrid-columnHeaders": {
            bgcolor: "grey.100",
            fontWeight: 700,
          },
          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
          },
        }}
      />

      <MDDialog
        title="Excluir organizador"
        message={`Deseja realmente excluir "${deleteTarget?.nomeFantasia?.toUpperCase() ?? ""}"? Esta ação não pode ser desfeita.`}
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        response={handleDeleteConfirm}
      />
    </MDBox>
  );
}
